import * as React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    Autocomplete,
    Alert as MuiAlert,
    useTheme,
} from "@mui/material";
import { FileUploadOutlined } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import Axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { tokens } from "../../theme";
import { getSubjects } from "../../utils/TextbookSubjects";
import { getAuthHeader } from "../../utils/authToken";
import { getCourseDuration } from "../../utils/CourseDuration";
import HiddenPDF from "../hwQuestions/HiddenPDF";
import Backdrop from "../../components/Backdrop";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ReplaceTextbookModal = ({
    open,
    onClose,
    textbook,
    onSuccess,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [file, setFile] = React.useState(null);
    const [numPages, setNumPages] = React.useState(null);
    const [pages, setPages] = React.useState(textbook?.pages ?? 0);
    const [subject1, setSubject1] = React.useState(null);
    const [subject2, setSubject2] = React.useState(null);
    const [subject3, setSubject3] = React.useState(null);
    const [subject4, setSubject4] = React.useState(null);
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const [linkedMaterials, setLinkedMaterials] = React.useState(null);
    const [linkedError, setLinkedError] = React.useState(false);

    const subjects = React.useMemo(() => getSubjects(), []);

    React.useEffect(() => {
        if (!open || !textbook) return;
        let cancelled = false;
        setLinkedMaterials(null);
        setLinkedError(false);
        Axios.get(
            `${process.env.REACT_APP_URL}/api/v1/textbooks/${textbook.id}/linked-class-materials`,
            { headers: { Authorization: getAuthHeader() } }
        ).then((res) => {
            if (!cancelled) setLinkedMaterials(res.data || []);
        }).catch(() => {
            if (!cancelled) setLinkedError(true);
        });
        return () => { cancelled = true; };
    }, [open, textbook]);

    const groupedByCourse = React.useMemo(() => {
        if (!linkedMaterials) return [];
        const map = new Map();
        for (const m of linkedMaterials) {
            const c = m.course;
            if (!c) continue;
            if (!map.has(c.id)) map.set(c.id, { course: c, materials: [] });
            map.get(c.id).materials.push(m);
        }
        return Array.from(map.values());
    }, [linkedMaterials]);

    React.useEffect(() => {
        if (!open || !textbook) return;
        setFile(null);
        setNumPages(null);
        setPages(textbook.pages ?? 0);
        const s1 = subjects.find((s) => s.value === textbook.subject_1) || null;
        setSubject1(s1);
        const s2 = s1?.subject2?.find((s) => s.value === textbook.subject_2) || null;
        setSubject2(s2);
        const s3 = s2?.subject3?.find((s) => s.value === textbook.subject_3) || null;
        setSubject3(s3);
        const s4 = s3?.subject4?.find((s) => s.value === textbook.subject_4) || null;
        setSubject4(s4);
    }, [open, textbook, subjects]);

    // When a new PDF is loaded and its detected page count differs from the
    // current value, auto-fill the field. Keeps the manual edit working: the
    // user can still override afterwards.
    React.useEffect(() => {
        if (file && numPages && Number(numPages) !== Number(pages)) {
            setPages(numPages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numPages]);

    const handleSubmit = async () => {
        setBackdropOpen(true);

        if (file && file.type !== "application/pdf") {
            setErrorAlertMessage("Please upload a PDF file");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }
        if (file && file.name.length > 150) {
            setErrorAlertMessage("File name should be less than 150 characters");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }
        if (
            file
            && numPages
            && Number(pages) !== Number(numPages)
            && !window.confirm("The number of pages you entered does not match the actual number of pages in the file. Continue?")
        ) {
            setBackdropOpen(false);
            return;
        }
        if (!subject1 || !subject2) {
            setErrorAlertMessage("Subjects 1 and 2 are required");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
            return;
        }

        const formData = new FormData();
        if (file) formData.append("file", file);
        formData.append("pages", pages);
        formData.append("subject_1", subject1.value);
        formData.append("subject_2", subject2.value);
        formData.append("subject_3", subject3?.value ?? "");
        formData.append("subject_4", subject4?.value ?? "");

        try {
            const response = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/textbooks/${textbook.id}`,
                formData,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            setSuccessAlertMessage(response.data.message || "Textbook replaced");
            setSuccessAlertOpen(true);
            setBackdropOpen(false);
            onSuccess?.();
            onClose();
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Something went wrong");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
        }
    };

    const documentRender = () => {
        const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);
        return (
            <Document
                file={file}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onContextMenu={(e) => e.preventDefault()}
            >
                <Page
                    pageNumber={1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={pixelRatio}
                />
            </Document>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Replace Textbook</DialogTitle>
            <DialogContent dividers>
                <Box
                    sx={{
                        bgcolor: colors.primary[600],
                        p: 2,
                        borderRadius: 2,
                        display: "grid",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Linked classes & materials
                        </Typography>
                        {linkedError ? (
                            <MuiAlert severity="error">Could not load linked classes.</MuiAlert>
                        ) : linkedMaterials === null ? (
                            <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        ) : groupedByCourse.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No classes are using this textbook yet.
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.5,
                                }}
                            >
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Linked to <b>{linkedMaterials.length}</b> class material
                                    {linkedMaterials.length === 1 ? '' : 's'} across{' '}
                                    <b>{groupedByCourse.length}</b> class
                                    {groupedByCourse.length === 1 ? '' : 'es'}.
                                </Typography>
                                {groupedByCourse.map(({ course, materials }) => (
                                    <Box key={course.id} sx={{ mb: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {course.code ? `${course.code}. ` : ''}{course.course_title}
                                        </Typography>
                                        {course.course_semesters && course.course_semesters.length > 0 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {getCourseDuration(course)}
                                            </Typography>
                                        )}
                                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                            {materials.map((m) => (
                                                <li key={m.id}>
                                                    <Typography variant="body2">{m.name}</Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    <MuiAlert severity="info">
                        Leave the file empty to update metadata only. Uploading a new PDF
                        keeps the textbook&apos;s ID and the old file is archived as
                        &quot;&lt;name&gt; old N.pdf&quot;.
                    </MuiAlert>

                    {file && (
                        <MuiAlert severity="warning">
                            Existing homework questions and page tags reference page numbers
                            from the previous PDF. If pagination has changed, those references
                            may now be misaligned.
                            {linkedMaterials && linkedMaterials.length > 0 && (
                                <> Replacing the PDF will overwrite the file URL and name on
                                the {linkedMaterials.length} linked class material
                                {linkedMaterials.length === 1 ? '' : 's'} listed above.</>
                            )}
                        </MuiAlert>
                    )}

                    <Box display="flex" alignItems="center" flexWrap="wrap">
                        <Typography variant="h6" mr={1}>
                            New PDF (optional)
                        </Typography>
                        <IconButton component="label">
                            <FileUploadOutlined />
                            <input
                                type="file"
                                hidden
                                accept="application/pdf"
                                onChange={(e) => {
                                    setFile(e.target.files[0] || null);
                                    setNumPages(null);
                                }}
                            />
                        </IconButton>
                        {file && (
                            <>
                                <Typography sx={{ ml: 1 }}>{file.name}</Typography>
                                <IconButton
                                    onClick={() => {
                                        setFile(null);
                                        setNumPages(null);
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {numPages && (
                        <Typography>Total {numPages} pages (detected)</Typography>
                    )}

                    <TextField
                        label="Total Pages"
                        type="number"
                        value={pages}
                        onChange={(e) => setPages(e.target.value)}
                    />

                    <Autocomplete
                        options={subjects}
                        value={subject1}
                        renderInput={(p) => <TextField {...p} label="Subject 1" />}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        onChange={(_, v) => {
                            setSubject1(v);
                            setSubject2(null);
                            setSubject3(null);
                            setSubject4(null);
                        }}
                    />
                    {subject1?.subject2 && (
                        <Autocomplete
                            options={subject1.subject2}
                            value={subject2}
                            renderInput={(p) => <TextField {...p} label="Subject 2" />}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            onChange={(_, v) => {
                                setSubject2(v);
                                setSubject3(null);
                                setSubject4(null);
                            }}
                        />
                    )}
                    {subject2?.subject3 && (
                        <Autocomplete
                            options={subject2.subject3}
                            value={subject3}
                            renderInput={(p) => <TextField {...p} label="Subject 3" />}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            onChange={(_, v) => {
                                setSubject3(v);
                                setSubject4(null);
                            }}
                        />
                    )}
                    {subject3?.subject4 && (
                        <Autocomplete
                            options={subject3.subject4}
                            value={subject4}
                            renderInput={(p) => <TextField {...p} label="Subject 4" />}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            onChange={(_, v) => setSubject4(v)}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="secondary">
                    Replace
                </Button>
            </DialogActions>
            <Backdrop
                backdropOpen={backdropOpen}
                handleBackdropClose={() => setBackdropOpen(false)}
            />
            {file && <HiddenPDF documentRender={documentRender} />}
        </Dialog>
    );
};

export default ReplaceTextbookModal;
